/**
 * THE PANEL'S SIDE OF LIVE AUDIO: one console listening to one call, and
 * perhaps taking it over. See docs/hitl.md §2 and §7.
 *
 * A plain object with `subscribe` / `getSnapshot`, for the same reason as
 * `CallSession`: it holds a socket, an AudioContext and maybe a microphone, and
 * must not be restarted by a re-render.
 *
 * THE SOCKET. `POST /api/sessions/:id/monitor` mints a short-lived monitor
 * token (valid only at upgrade time), then `/ws/monitor/:id?t=` carries JSON
 * messages both ways and live frames as binary. A dropped socket reconnects
 * with backoff and a NEW token each time, because the old one has expired.
 *
 * WHO HOLDS THE CALL. The DO decides, and it only accepts `Human` frames from
 * the console that holds it; this side must also never send them otherwise.
 * The DO's messages say who holds the call by display name (the panel email),
 * not "you". So this console counts itself the holder when it asked
 * (`{t:'takeover'}`) AND the `started` answer names it — or names nobody, from
 * a DO that does not say. Two consoles racing both see `started`; the one the
 * DO turned away sees another name, then its own `refused`. After a reconnect
 * the holder is recognised by the same name in `monitor.ready`.
 *
 * THE MIC OPENS BEFORE THE TAKEOVER IS ASKED FOR. A takeover hangs up the AI,
 * so taking a call with a microphone that then turns out to be blocked would
 * leave the caller talking to nobody. If the mic cannot be opened, nothing is
 * sent.
 */
import { LiveSource, decodeLiveFrame, encodeLiveFrame } from '@voiceai/wire'
import type { LiveAudioStatus, MonitorClientMsg, MonitorServerMsg, SessionState } from '@voiceai/wire'
import { socketUrl } from '../control'
import { openMicrophone } from '../transport'
import { mergeTranscriptLine } from '../transcript-merge'
import type { TranscriptLine } from '../types'
import { LivePlayer, LiveTap } from './audio'
import type { LiveLog } from './audio'

export type MonitorVoice = 'caller' | 'agent' | 'human'

const VOICE_SOURCE: Record<MonitorVoice, LiveSource> = {
  caller: LiveSource.Caller,
  agent: LiveSource.Agent,
  human: LiveSource.Human,
}

/** `POST /api/sessions/:id/monitor`, per docs/hitl.md §2. */
export interface MonitorToken {
  url: string
  token: string
  canTakeOver: boolean
  expiresInSec: number
}

/** The sound half, behind an interface so the message handling can be tested
 *  without a browser. `openBrowserMonitorAudio` below is the real one. */
export interface MonitorAudio {
  play(source: LiveSource, pcm: Int16Array): void
  setMuted(source: LiveSource, muted: boolean): void
  /** Open the person's mic and deliver 20 ms frames until `closeMic`. Rejects
   *  when the mic cannot be had. */
  openMic(onFrame: (pcm: Int16Array) => void): Promise<void>
  closeMic(): void
  readonly path: 'loopback' | 'direct'
  close(): void
}

export interface MonitorDeps {
  getToken(signal: AbortSignal): Promise<MonitorToken>
  /** Called once, from inside the click that starts listening, so the
   *  AudioContext it makes is allowed to run. */
  openAudio(): Promise<MonitorAudio>
  /** This console's display name as the DO reports holders, or null. */
  me: string | null
  /**
   * Turn a failed token request into a sentence, and say whether trying again
   * could help (`final`: a read-only account, a session that has gone).
   */
  describeTokenError(e: unknown): { message: string; final: boolean; ended?: boolean }
  onLog?: LiveLog
}

export type MonitorPhase = 'idle' | 'connecting' | 'listening' | 'reconnecting' | 'ended' | 'failed'

export interface MonitorSnapshot {
  phase: MonitorPhase
  /** Why listening stopped or cannot start, in plain words. */
  error: string | null
  transport: 'webrtc' | 'sip' | null
  state: SessionState | null
  audio: LiveAudioStatus | null
  audioReason: string | null
  takenOverBy: string | null
  canTakeOver: boolean
  /** This console holds the call: its mic is what the caller hears. */
  holding: boolean
  /** A takeover asked for and not answered yet. */
  takeoverPending: boolean
  /** Why the last takeover did not happen, in plain words. */
  takeoverProblem: string | null
  micOn: boolean
  muted: Record<MonitorVoice, boolean>
  transcript: readonly TranscriptLine[]
  playback: 'loopback' | 'direct' | null
}

const INITIAL: MonitorSnapshot = {
  phase: 'idle',
  error: null,
  transport: null,
  state: null,
  audio: null,
  audioReason: null,
  takenOverBy: null,
  canTakeOver: false,
  holding: false,
  takeoverPending: false,
  takeoverProblem: null,
  micOn: false,
  muted: { caller: false, agent: false, human: false },
  transcript: [],
  playback: null,
}

const RECONNECT_MIN_MS = 250
const RECONNECT_MAX_MS = 8000
const MAX_TRANSCRIPT_LINES = 600
/** See the same bound in link.ts. */
const MAX_BUFFERED_BYTES = 64 * 1024

function isEnded(state: SessionState): boolean {
  return state.startsWith('ENDED') || state === 'FAILED'
}

/**
 * A takeover refusal in plain words. The codes are the DO's; an unknown one is
 * shown as it is rather than guessed at.
 */
export function takeoverProblemText(reason: string | undefined, state: 'refused' | 'failed'): string {
  switch (reason) {
    case 'already_taken_over':
    case 'taken_over':
      return 'Somebody else has already taken over this call.'
    case 'not_allowed':
    case 'forbidden':
    case 'read_only':
      return 'Your account is not allowed to take over calls.'
    case 'not_live':
    case 'ended':
      return 'The call has already ended.'
    case 'caller_not_connected':
    case 'no_live_audio':
    case 'live_not_connected':
    case 'caller_page_without_live_audio':
      return 'The caller’s page is not connected for live audio, so they would not hear you. The AI still has the call.'
    case 'no_call_sid':
      return 'This phone call cannot be taken over: the carrier’s call id is missing. The AI still has the call.'
    case 'provider_hangup_failed':
      return 'You have the call, but the AI’s line could not be closed, so the AI may still hear the caller. It is closed again when the call ends.'
    default:
      return state === 'failed'
        ? `The takeover did not complete${reason ? ` (${reason})` : ''}. The AI may still be on the line.`
        : `The call was not taken over${reason ? ` (${reason})` : ''}.`
  }
}

/** Why live audio is unavailable, in plain words. */
export function audioReasonText(reason: string | null): string | null {
  if (reason === null) return null
  switch (reason) {
    case 'caller_page_without_live_audio':
      return 'the caller’s page is an older version that cannot send live audio'
    case 'no_call_sid':
      return 'this phone call has no carrier call id to listen to'
    case 'twilio_not_configured':
      return 'phone listen-in is not set up on this server (the Twilio credentials are missing)'
    case 'twilio_stream_failed':
      return 'the phone company’s audio stream could not be started'
    case 'twilio_stream_ended':
      return 'the phone company’s audio stream ended'
    case 'caller_live_socket_closed':
      return 'the caller’s live audio connection dropped; waiting for it to come back'
    default:
      return reason.replaceAll('_', ' ')
  }
}

export class MonitorClient {
  private snap: MonitorSnapshot = INITIAL
  private readonly listeners = new Set<() => void>()
  private ws: WebSocket | null = null
  private audio: MonitorAudio | null = null
  private attempt = 0
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private abort: AbortController | null = null
  /** Bumped by every `close`, so an await that outlives one does nothing. */
  private generation = 0
  private humanSeq = 0

  constructor(private readonly deps: MonitorDeps) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  getSnapshot = (): MonitorSnapshot => this.snap

  private set(patch: Partial<MonitorSnapshot>): void {
    this.snap = { ...this.snap, ...patch }
    for (const l of this.listeners) l()
  }

  private log(level: 'info' | 'warn' | 'error', message: string): void {
    this.deps.onLog?.(level, message)
  }

  /** Start listening. Call it from the click itself: see `MonitorDeps.openAudio`. */
  async listen(): Promise<void> {
    if (this.snap.phase !== 'idle' && this.snap.phase !== 'ended' && this.snap.phase !== 'failed') return
    const gen = ++this.generation
    this.set({ ...INITIAL, muted: this.snap.muted, phase: 'connecting' })
    let audio: MonitorAudio
    try {
      audio = await this.deps.openAudio()
    } catch (e) {
      if (gen !== this.generation) return
      this.set({ phase: 'failed', error: `The audio could not be started in this browser: ${describe(e)}` })
      return
    }
    if (gen !== this.generation) {
      audio.close()
      return
    }
    this.audio = audio
    for (const voice of Object.keys(this.snap.muted) as MonitorVoice[]) {
      audio.setMuted(VOICE_SOURCE[voice], this.snap.muted[voice])
    }
    this.set({ playback: audio.path })
    await this.connect(gen)
  }

  /**
   * Ask to take the call. Opens the mic first (see the header); the answer
   * arrives as a `takeover` message.
   */
  async takeOver(): Promise<void> {
    const s = this.snap
    if (s.phase !== 'listening' || !s.canTakeOver || s.holding || s.takeoverPending || s.takenOverBy !== null) return
    const audio = this.audio
    if (!audio) return
    const gen = this.generation
    this.set({ takeoverPending: true, takeoverProblem: null })
    try {
      await audio.openMic((pcm) => this.onMicFrame(pcm))
    } catch (e) {
      if (gen !== this.generation) return
      this.set({
        takeoverPending: false,
        takeoverProblem: `Your microphone could not be opened, so the call was not taken over: ${describe(e)}`,
      })
      return
    }
    if (gen !== this.generation) return
    this.set({ micOn: true })
    if (!this.sendJson({ t: 'takeover' })) {
      this.closeMic()
      this.set({ takeoverPending: false, takeoverProblem: 'The connection to the call dropped. Try again in a moment.' })
    }
  }

  /**
   * Pick the call up again after this console lost it — a page reload, most
   * likely — while the DO still names this person as the holder. The DO waits
   * 30 s for the holder's user to come back (docs/hitl.md §6), so nothing is
   * asked for: the mic opens and this console counts as the holder again.
   */
  async resumeHolding(): Promise<void> {
    const s = this.snap
    const me = this.deps.me
    if (s.phase !== 'listening' || s.holding || me === null || s.takenOverBy !== me) return
    const audio = this.audio
    if (!audio) return
    const gen = this.generation
    try {
      await audio.openMic((pcm) => this.onMicFrame(pcm))
    } catch (e) {
      if (gen !== this.generation) return
      this.set({ takeoverProblem: `Your microphone could not be opened: ${describe(e)}` })
      return
    }
    if (gen !== this.generation) return
    this.humanSeq = 0
    this.set({ holding: true, micOn: true, takeoverProblem: null })
  }

  /** End the call for everyone. Only the holder may; the DO checks too. */
  hangup(): void {
    if (!this.snap.holding) return
    this.sendJson({ t: 'hangup' })
  }

  setMuted(voice: MonitorVoice, muted: boolean): void {
    this.set({ muted: { ...this.snap.muted, [voice]: muted } })
    this.audio?.setMuted(VOICE_SOURCE[voice], muted)
  }

  /** Stop listening. A holder's call is NOT ended by this; the DO waits for
   *  the holder to come back before it gives up (docs/hitl.md §6). */
  close(): void {
    this.generation++
    this.teardown()
    this.set({ phase: 'idle', holding: false, takeoverPending: false, micOn: false })
  }

  // --------------------------------------------------------------------------

  private async connect(gen: number): Promise<void> {
    if (gen !== this.generation) return
    const ac = new AbortController()
    this.abort = ac
    let token: MonitorToken
    try {
      token = await this.deps.getToken(ac.signal)
    } catch (e) {
      if (gen !== this.generation) return
      const d = this.deps.describeTokenError(e)
      if (d.final) {
        this.teardown()
        this.set({ phase: d.ended ? 'ended' : 'failed', error: d.message, holding: false, micOn: false })
        return
      }
      this.set({ error: d.message })
      this.scheduleReconnect(gen)
      return
    }
    if (gen !== this.generation) return
    const ws = new WebSocket(socketUrl(token.url, token.token))
    ws.binaryType = 'arraybuffer'
    this.ws = ws
    ws.onmessage = (ev: MessageEvent) => {
      if (gen !== this.generation) return
      if (typeof ev.data === 'string') this.onText(ev.data)
      else if (ev.data instanceof ArrayBuffer) this.onFrame(ev.data)
    }
    ws.onclose = () => {
      if (this.ws === ws) this.ws = null
      if (gen !== this.generation) return
      const phase = this.snap.phase
      if (phase === 'ended' || phase === 'failed' || phase === 'idle') return
      this.set({ phase: 'reconnecting' })
      this.scheduleReconnect(gen)
    }
    ws.onerror = () => {}
  }

  private scheduleReconnect(gen: number): void {
    if (gen !== this.generation || this.reconnectTimer !== null) return
    const delay = Math.min(RECONNECT_MAX_MS, RECONNECT_MIN_MS * 2 ** this.attempt)
    this.attempt += 1
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.connect(gen)
    }, delay * (0.7 + Math.random() * 0.6))
  }

  private onText(text: string): void {
    let msg: MonitorServerMsg
    try {
      msg = JSON.parse(text) as MonitorServerMsg
    } catch {
      this.log('warn', 'monitor: unparseable message discarded')
      return
    }
    switch (msg.t) {
      case 'monitor.ready': {
        this.attempt = 0
        // A holder that reconnected is still the holder when the DO names it;
        // anybody else is not, whatever this console believed before.
        const holding =
          this.snap.holding && msg.takenOverBy !== null && (this.deps.me === null || msg.takenOverBy === this.deps.me)
        if (!holding && this.snap.holding) this.closeMic()
        this.set({
          phase: isEnded(msg.state) ? 'ended' : 'listening',
          error: null,
          transport: msg.transport,
          state: msg.state,
          audio: msg.audio,
          audioReason: null,
          takenOverBy: msg.takenOverBy,
          canTakeOver: msg.canTakeOver,
          holding,
        })
        if (isEnded(msg.state)) this.finish()
        break
      }
      case 'audio':
        this.set({ audio: msg.status, audioReason: msg.reason ?? null })
        break
      case 'state':
        this.set({ state: msg.state })
        if (isEnded(msg.state)) {
          this.set({ phase: 'ended' })
          this.finish()
        }
        break
      case 'transcript': {
        const next = mergeTranscriptLine(
          this.snap.transcript, msg.itemId, msg.who, msg.text, msg.final, Date.now(), MAX_TRANSCRIPT_LINES,
        )
        if (next) this.set({ transcript: next })
        break
      }
      case 'takeover':
        this.onTakeoverMsg(msg)
        break
      case 'error':
        this.log('warn', `monitor: ${msg.code}: ${msg.message}`)
        this.set({ error: msg.message })
        break
    }
  }

  private onTakeoverMsg(msg: Extract<MonitorServerMsg, { t: 'takeover' }>): void {
    if (msg.state === 'started') {
      const mine = this.snap.takeoverPending && (msg.by === undefined || this.deps.me === null || msg.by === this.deps.me)
      if (mine) this.humanSeq = 0
      this.set({
        takenOverBy: msg.by ?? this.snap.takenOverBy ?? (mine ? (this.deps.me ?? 'you') : 'somebody'),
        holding: this.snap.holding || mine,
        // Still pending when another console won: its own refusal is on the way.
        takeoverPending: mine ? false : this.snap.takeoverPending,
        takeoverProblem: null,
      })
      return
    }
    const holdingStill = msg.state === 'refused' && this.snap.holding
    if (!holdingStill) this.closeMic()
    this.set({
      takeoverPending: false,
      holding: holdingStill,
      takenOverBy: msg.state === 'failed' ? null : this.snap.takenOverBy,
      takeoverProblem: takeoverProblemText(msg.reason, msg.state),
    })
  }

  private onFrame(buf: ArrayBuffer): void {
    let frame
    try {
      frame = decodeLiveFrame(buf)
    } catch {
      return
    }
    // A holder's own voice must never come back to it; the DO should not send
    // it, and if it does it would be an echo half a second late.
    if (frame.source === LiveSource.Human && this.snap.holding) return
    this.audio?.play(frame.source, frame.pcm)
  }

  /** The person's mic, every 20 ms. Sent ONLY while this console holds the call. */
  private onMicFrame(pcm: Int16Array): void {
    if (!this.snap.holding) return
    const seq = this.humanSeq++
    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN || ws.bufferedAmount > MAX_BUFFERED_BYTES) return
    try {
      ws.send(encodeLiveFrame(LiveSource.Human, seq, pcm))
    } catch {
      /* closing under us; onclose reconnects */
    }
  }

  private sendJson(msg: MonitorClientMsg): boolean {
    const ws = this.ws
    if (!ws || ws.readyState !== WebSocket.OPEN) return false
    try {
      ws.send(JSON.stringify(msg))
      return true
    } catch {
      return false
    }
  }

  private closeMic(): void {
    this.audio?.closeMic()
    if (this.snap.micOn) this.set({ micOn: false })
  }

  /** The call ended: stop everything but keep what was shown. */
  private finish(): void {
    this.generation++
    this.teardown()
    this.set({ holding: false, takeoverPending: false, micOn: false })
  }

  private teardown(): void {
    if (this.reconnectTimer !== null) clearTimeout(this.reconnectTimer)
    this.reconnectTimer = null
    this.abort?.abort()
    this.abort = null
    const ws = this.ws
    this.ws = null
    if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
      try {
        ws.close(1000, 'stopped listening')
      } catch {
        /* nothing to do */
      }
    }
    this.audio?.closeMic()
    this.audio?.close()
    this.audio = null
    this.attempt = 0
  }
}

function describe(e: unknown): string {
  return e instanceof Error ? e.message : String(e)
}

/**
 * The real sound half: a 48 kHz AudioContext made inside the click, the shared
 * player with its echo-cancelled loopback, and the person's mic tapped into
 * `Human` frames. The mic uses the same processing constraints as a caller's
 * (echo cancellation, noise suppression, gain control, all `ideal`), because
 * the person is on speakers as often as a caller is.
 */
export async function openBrowserMonitorAudio(log: LiveLog): Promise<MonitorAudio> {
  // Created and resumed BEFORE any await, so it is inside the click. A context
  // made later may stay suspended, and presents as silence.
  const ctx = new AudioContext({ sampleRate: 48000, latencyHint: 'interactive' })
  const resumed = ctx.resume()
  let player: LivePlayer
  try {
    await resumed
    player = await LivePlayer.open(ctx, log)
  } catch (e) {
    void ctx.close().catch(() => {})
    throw e
  }
  let mic: MediaStream | null = null
  let tap: LiveTap | null = null
  const closeMic = () => {
    tap?.stop()
    tap = null
    for (const t of mic?.getTracks() ?? []) t.stop()
    mic = null
  }
  return {
    play: (source, pcm) => player.push(source, pcm),
    setMuted: (source, muted) => player.setMuted(source, muted),
    async openMic(onFrame) {
      if (tap) return
      const stream = await openMicrophone()
      mic = stream
      try {
        tap = await LiveTap.open(ctx, stream, onFrame)
      } catch (e) {
        closeMic()
        throw e
      }
    },
    closeMic,
    get path() {
      return player.path
    },
    close() {
      closeMic()
      player.close()
      void ctx.close().catch(() => {})
    },
  }
}
